import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "spiruva_admin_token";
const TOKEN_EXP_KEY = "spiruva_admin_token_exp";
const TOKEN_EMAIL_KEY = "spiruva_admin_email";

export const getAdminToken = () => sessionStorage.getItem(TOKEN_KEY);
const _setAdminSession = ({ token, expires_at, email }) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  if (expires_at) sessionStorage.setItem(TOKEN_EXP_KEY, expires_at);
  if (email) sessionStorage.setItem(TOKEN_EMAIL_KEY, email);
};
export const getAdminEmail = () => sessionStorage.getItem(TOKEN_EMAIL_KEY) || "";
export const getAdminTokenExpiry = () => sessionStorage.getItem(TOKEN_EXP_KEY) || "";
export const clearAdminToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXP_KEY);
  sessionStorage.removeItem(TOKEN_EMAIL_KEY);
};
export const isAdminAuthed = () => {
  const t = getAdminToken();
  if (!t) return false;
  const exp = getAdminTokenExpiry();
  if (exp) {
    try {
      if (new Date(exp).getTime() < Date.now()) {
        clearAdminToken();
        return false;
      }
    } catch { /* ignore parse errors */ }
  }
  return true;
};

const adminHeaders = () => {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Global 401 handler — clears session, redirects to /admin
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && (url.includes("/blog/admin/") || url.includes("/admin/"))) {
      clearAdminToken();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin")) {
        window.location.assign("/admin");
      } else if (typeof window !== "undefined" && window.location.pathname !== "/admin") {
        window.location.assign("/admin");
      }
    }
    return Promise.reject(err);
  }
);

// ---------- public ----------
export const fetchPosts = async ({ category, tag, featuredOnly, search } = {}) => {
  const params = {};
  if (category && category !== "All") params.category = category;
  if (tag) params.tag = tag;
  if (featuredOnly) params.featured_only = true;
  if (search) params.search = search;
  const { data } = await axios.get(`${API}/blog/posts`, { params });
  return data.posts;
};

export const fetchPost = async (slug) => {
  const { data } = await axios.get(`${API}/blog/posts/${slug}`);
  return data;
};

export const subscribe = async (email, name) => {
  const { data } = await axios.post(`${API}/blog/subscribe`, { email, name });
  return data;
};

// ---------- admin ----------
export const adminLogin = async (email, password) => {
  const { data } = await axios.post(`${API}/admin/login`, { email, password });
  _setAdminSession({ token: data.token, expires_at: data.expires_at, email: data.email });
  return data;
};

export const adminMe = async () => {
  const { data } = await axios.get(`${API}/admin/me`, { headers: adminHeaders() });
  return data;
};

export const adminFetchPosts = async () => {
  const { data } = await axios.get(`${API}/blog/admin/posts`, { headers: adminHeaders() });
  return data.posts;
};

export const adminCreatePost = async (payload) => {
  const { data } = await axios.post(`${API}/blog/admin/posts`, payload, { headers: adminHeaders() });
  return data.post;
};

export const adminUpdatePost = async (id, payload) => {
  const { data } = await axios.put(`${API}/blog/admin/posts/${id}`, payload, { headers: adminHeaders() });
  return data.post;
};

export const adminDeletePost = async (id) => {
  const { data } = await axios.delete(`${API}/blog/admin/posts/${id}`, { headers: adminHeaders() });
  return data;
};

export const adminBulkAction = async (ids, action) => {
  const { data } = await axios.post(
    `${API}/blog/admin/posts/bulk`,
    { ids, action },
    { headers: adminHeaders() }
  );
  return data;
};

export const adminFetchLeads = async () => {
  const { data } = await axios.get(`${API}/blog/admin/leads`, { headers: adminHeaders() });
  return data.leads;
};

export const adminFetchDownloadLeads = async () => {
  const { data } = await axios.get(`${API}/admin/leads/downloads`, { headers: adminHeaders() });
  return data; // { leads: [...], recent_downloads: [...], total_leads }
};

export const adminFetchNewsletterSubscribers = async () => {
  const { data } = await axios.get(`${API}/admin/newsletter/subscribers`, { headers: adminHeaders() });
  return data; // { subscribers: [...], total }
};

export const adminFetchCampaigns = async () => {
  const { data } = await axios.get(`${API}/admin/newsletter/campaigns`, { headers: adminHeaders() });
  return data; // { campaigns: [...], total }
};

export const adminGenerateCampaign = async () => {
  const { data } = await axios.post(`${API}/admin/newsletter/campaigns/generate`, {}, { headers: adminHeaders() });
  return data; // { ok, campaign }
};

export const adminApproveCampaign = async (campaignId, schedule) => {
  // schedule (optional): { run_at: ISO-8601 with offset, timezone: "Asia/Tokyo" }
  const body = schedule && schedule.run_at ? { run_at: schedule.run_at, timezone: schedule.timezone || "UTC" } : {};
  const { data } = await axios.post(`${API}/admin/newsletter/campaigns/${campaignId}/approve`, body, { headers: adminHeaders() });
  return data; // { ok, scheduled, ... }
};

export const adminUnscheduleCampaign = async (campaignId) => {
  const { data } = await axios.post(`${API}/admin/newsletter/campaigns/${campaignId}/unschedule`, {}, { headers: adminHeaders() });
  return data;
};

export const adminCancelCampaign = async (campaignId) => {
  const { data } = await axios.post(`${API}/admin/newsletter/campaigns/${campaignId}/cancel`, {}, { headers: adminHeaders() });
  return data; // { ok }
};

export const adminFetchInquiries = async () => {
  const { data } = await axios.get(`${API}/admin/inquiries`, { headers: adminHeaders() });
  return data;
};

export const adminFetchDistributorApplications = async () => {
  const { data } = await axios.get(`${API}/admin/distributor-applications`, { headers: adminHeaders() });
  return data;
};

export const adminFetchAnalytics = async () => {
  const { data } = await axios.get(`${API}/blog/admin/analytics`, { headers: adminHeaders() });
  return data;
};

export const adminFetchDigestPreview = async (periodDays = 7) => {
  const { data } = await axios.get(`${API}/admin/digest/preview`, {
    headers: adminHeaders(),
    params: { period_days: periodDays },
  });
  return data;
};

export const adminSendDigest = async (periodDays = 7) => {
  const { data } = await axios.post(`${API}/admin/digest/send?period_days=${periodDays}`, {}, {
    headers: adminHeaders(),
  });
  return data;
};

export const adminFetchDigestLast = async () => {
  const { data } = await axios.get(`${API}/admin/digest/last`, { headers: adminHeaders() });
  return data.last;
};

export const trackBlogEvent = async (payload) => {
  try {
    await axios.post(`${API}/blog/track`, payload);
  } catch { /* analytics must never block UX */ }
};

export const adminChangePassword = async (current_password, new_password) => {
  const { data } = await axios.post(
    `${API}/admin/change-password`,
    { current_password, new_password },
    { headers: adminHeaders() }
  );
  // Server issues a fresh token after a successful change
  if (data?.token && data?.expires_at) {
    _setAdminSession({ token: data.token, expires_at: data.expires_at, email: getAdminEmail() });
  }
  return data;
};

export const adminSitemapPing = async () => {
  const { data } = await axios.post(`${API}/admin/sitemap-ping`, {}, { headers: adminHeaders() });
  return data;
};

export const CATEGORIES = [
  "Formulation & Applications",
  "Science & Quality",
  "Market Intelligence",
  "E40 Insights",
];

export const CATEGORY_COLOR = {
  "Formulation & Applications": { hex: "#2d6a4f", from: "#2d6a4f", to: "#1a4d2e", text: "#f8f5ef" },
  "Science & Quality":          { hex: "#006d77", from: "#006d77", to: "#003b44", text: "#f8f5ef" },
  "Market Intelligence":        { hex: "#1a4d2e", from: "#1a4d2e", to: "#0f3a20", text: "#f8f5ef" },
  "E40 Insights":               { hex: "#c9a84c", from: "#c9a84c", to: "#8a6e1f", text: "#0f1f14" },
};

export const categoryGradient = (cat) => {
  const c = CATEGORY_COLOR[cat] || CATEGORY_COLOR["Market Intelligence"];
  return `linear-gradient(135deg, ${c.from} 0%, ${c.to} 100%)`;
};
